import type { JSX } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { AuthPage } from '@/features/auth';
import { Home } from '@/app/home/Home';
import { TraceabilityScan, TraceabilityEntry, WorkshopPage } from '@/features/traceability';
import { Analytics, ReferenceComparisonPage, AnalyticsDashboard } from '@/features/analytics';
import { MLRulPage } from '@/features/machine-learning';
import { SignalsPage } from '@/features/signals';
import { LabelingPage } from '@/features/labelize';
import { ToolRecommenderPage, ExperimentDetailPage } from '@/features/tool-recommender';
import { PrivateRoute } from '@/components/shared/routing/PrivateRoute';
import { ModuleRoute } from '@/components/shared/routing/ModuleRoute';
import { TRACEABILITY, ANALYTICS, ML, TOOL_RECOMMENDER } from '@/constants/modules';
import type { UserModules } from '@/constants/modules';
import { UploadImage } from '@/features/traceability/pages/UploadImage';
import { AdminRoute } from './AdminRoute';
import { AdminPage } from '@/features/admin/pages/AdminPage';
import { BackendConfigPage } from '@/features/admin/pages/BackendConfigPage';
import { PermissionErrorPage } from '@/features/admin/pages/PermissionErrorPage';
import { ToolListPage } from '@/features/toolView/pages/ToolListPage';
import { ToolDetailPage } from '@/features/toolView/pages/ToolDetailPage';
import { ProfilePage } from '@/app/settings/ProfilePage';
import { ContactPage } from '@/app/contact/ContactPage';
import { FeatureErrorBoundary } from '@/components/errors';
import { AuthenticatedShell } from '@/components/shared/AuthenticatedShell';

/**
 * A feature page inside its own error boundary, gated on a user module when one is given
 * (`/labelize` is the one feature route without a module gate).
 */
function featureRoute(
  path: string,
  featureName: string,
  page: JSX.Element,
  module?: UserModules
): RouteObject {
  const guarded = <FeatureErrorBoundary featureName={featureName}>{page}</FeatureErrorBoundary>;
  return { path, element: module ? <ModuleRoute module={module}>{guarded}</ModuleRoute> : guarded };
}

/** Same, behind the admin check instead of a module. */
function adminRoute(path: string, featureName: string, page: JSX.Element): RouteObject {
  return {
    path,
    element: (
      <AdminRoute>
        <FeatureErrorBoundary featureName={featureName}>{page}</FeatureErrorBoundary>
      </AdminRoute>
    ),
  };
}

export const router = createBrowserRouter([
  {
    path: '/auth',
    element: <AuthPage />,
  },
  {
    element: <PrivateRoute />,
    children: [
      {
        element: <AuthenticatedShell />,
        children: [
          {
            path: '/',
            element: <Home />,
          },
          {
            path: '/profile',
            element: <ProfilePage />,
          },
          {
            path: '/contact',
            element: <ContactPage />,
          },
          {
            path: '/permission-error',
            element: <PermissionErrorPage />,
          },
          featureRoute('/traceability/scan', 'Traceability', <TraceabilityScan />, TRACEABILITY),
          featureRoute('/traceability/uploadImage', 'Traceability', <UploadImage />, TRACEABILITY),
          featureRoute('/traceability/form', 'Traceability', <TraceabilityEntry />, TRACEABILITY),
          featureRoute('/analytics', 'Analytics', <AnalyticsDashboard />, ANALYTICS),
          featureRoute('/analytics/classic', 'Analytics', <Analytics />, ANALYTICS),
          featureRoute(
            '/analytics/comparison/:reference?',
            'Analytics',
            <ReferenceComparisonPage />,
            ANALYTICS
          ),
          featureRoute('/ml', 'Machine Learning', <MLRulPage />, ML),
          featureRoute('/signals', 'Signals', <SignalsPage />, ML),
          featureRoute('/traceability/tools', 'Tool View', <ToolListPage />, TRACEABILITY),
          featureRoute(
            '/traceability/tools/:toolId',
            'Tool View',
            <ToolDetailPage />,
            TRACEABILITY
          ),
          featureRoute('/traceability/workshop', 'Traceability', <WorkshopPage />, TRACEABILITY),
          featureRoute('/labelize', 'Labelize', <LabelingPage />),
          featureRoute(
            '/tool-recommender',
            'Tool Recommender',
            <ToolRecommenderPage />,
            TOOL_RECOMMENDER
          ),
          featureRoute(
            '/tool-recommender/experiments/:experimentId',
            'Tool Recommender',
            <ExperimentDetailPage />,
            TOOL_RECOMMENDER
          ),
          adminRoute('/admin', 'Admin', <AdminPage />),
          adminRoute('/admin/backend-config', 'Admin', <BackendConfigPage />),
          // Unknown URL: back to the home page rather than React Router's developer error screen.
          // Inside PrivateRoute, so a visitor who is not logged in still lands on /auth.
          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);
